import type { HeatmapCacheValue, IHeatmapDataStoreState } from '../../stores';
import type { BaseHeaderResponse } from '~/api/types/graphqlClient';
import type { UseQueryOptions } from '@tanstack/react-query';
import type { IHeatmapBaseWhereInput } from '../types';

import { BaseHeaderXCache } from '~/api/types/graphqlClient';
import { getCacheParameterKey } from '~/modules/analytics';
import { useBaseCachedQuery } from '~/modules/apps';
import { useHeatmapDataStore } from '../../stores';

export interface IFetchHeatmapDataProps {
  whereInput: IHeatmapBaseWhereInput;
  enabled?: boolean;
}

interface IHeatmapCachedQueryConfig<TData, TResponse extends { headers: BaseHeaderResponse }> {
  cacheSelector: (state: IHeatmapDataStoreState) => Record<string, HeatmapCacheValue<TData>>;
  upsertSelector: (
    state: IHeatmapDataStoreState,
  ) => (key: string, value: HeatmapCacheValue<TData>) => void;
  getResponseData: (response: TResponse) => TData;
  useQuery: (
    variables: { where: IHeatmapBaseWhereInput },
    options: UseQueryOptions<TResponse, TData>,
  ) => void;
}

export const useHeatmapBaseCachedQuery = <TData, TResponse extends { headers: BaseHeaderResponse }>(
  props: IFetchHeatmapDataProps,
  config: IHeatmapCachedQueryConfig<TData, TResponse>,
) => {
  const { whereInput, enabled = true } = props;

  const parameterKey = getCacheParameterKey(whereInput);
  const cache = useHeatmapDataStore(config.cacheSelector);
  const upsertCache = useHeatmapDataStore(config.upsertSelector);

  return useBaseCachedQuery<TData, TResponse, { where: IHeatmapBaseWhereInput }>({
    cache,
    xCache: BaseHeaderXCache.HIT, // TODO: Remove this once we have a proper cache system
    upsertCache,
    parameterKey,
    enabled,
    getResponseData: config.getResponseData,
    useQuery: config.useQuery,
    variables: { where: whereInput },
  });
};
